// bluaIQ — Landing Page
// Bilingual (ES default for DFW market, toggleable to EN via header button)
// Language source of truth: localStorage['bluaiq-lang'] (wired via i18next)
// 2026 SaaS design: story-driven hero, bold Plus Jakarta Sans typography,
// split-screen layouts, real product previews, conversion-optimized CTAs
// Brand: blua (black #1C1A17) + IQ (blue #06539B) + tagline gray #9E9890

import { useState, useEffect, useRef, useCallback, ReactNode } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { SignedIn, SignedOut, SignUpButton } from '@clerk/clerk-react'
import { getLandingPlans } from '../shared/plans'
import {
  FileText, DollarSign, Hammer, BarChart3, Shield, ScrollText,
  CreditCard, Camera, Mail, Zap, Globe, Check,
  ArrowRight, Pause, Star, Clock, TrendingUp,
  Smartphone, Menu, X, ChevronRight, Play,
  Upload, Search, Building2, Users, CalendarDays, HelpCircle, ChevronDown, MessageSquare
} from 'lucide-react'
// DEMO_PROJECT.id is the canonical demo project route target. We
// import it from the shared demoData helper instead of hardcoding
// 'demo-frisco-01' here so future demo-project-id changes only need
// to land in one place.
import { DEMO_PROJECT } from '../utils/demoData'

const hasClerk = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

// Signup CTA: shows Clerk modal for signed-out users, goes to /projects for signed-in users.
// Falls back to plain onClick if Clerk is not configured (dev without VITE_CLERK_PUBLISHABLE_KEY).
function SignupCTA({
  className,
  children,
  onSignedInClick,
  onBeforeOpen,
}: {
  className?: string
  children: ReactNode
  onSignedInClick: () => void
  onBeforeOpen?: () => void
}) {
  if (!hasClerk) {
    return (
      <button onClick={() => { onBeforeOpen?.(); onSignedInClick() }} className={className}>
        {children}
      </button>
    )
  }
  return (
    <>
      <SignedOut>
        <SignUpButton mode="modal" forceRedirectUrl="/projects" signInForceRedirectUrl="/projects">
          <button onClick={onBeforeOpen} className={className}>{children}</button>
        </SignUpButton>
      </SignedOut>
      <SignedIn>
        <button onClick={() => { onBeforeOpen?.(); onSignedInClick() }} className={className}>
          {children}
        </button>
      </SignedIn>
    </>
  )
}

// ── DATA ──────────────────────────────────────────

// Pricing cards derive from the shared plan catalog (src/shared/plans.ts) —
// same keys/prices/limits as before; change pricing there, not here.
const PLANS = getLandingPlans()

const FEATURES = [
  { key: 'takeoffs', icon: FileText, bg: 'bg-blue-50', iconBg: 'bg-blue-100', iconColor: 'text-[#06539B]' },
  { key: 'quotes', icon: DollarSign, bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600' },
  { key: 'bids', icon: Hammer, bg: 'bg-orange-50', iconBg: 'bg-orange-100', iconColor: 'text-orange-600' },
  { key: 'budget', icon: BarChart3, bg: 'bg-purple-50', iconBg: 'bg-purple-100', iconColor: 'text-purple-600' },
  { key: 'contracts', icon: ScrollText, bg: 'bg-rose-50', iconBg: 'bg-rose-100', iconColor: 'text-rose-600' },
  { key: 'insurance', icon: Shield, bg: 'bg-amber-50', iconBg: 'bg-amber-100', iconColor: 'text-amber-600' },
  { key: 'photos', icon: Camera, bg: 'bg-cyan-50', iconBg: 'bg-cyan-100', iconColor: 'text-cyan-600' },
  { key: 'emails', icon: Mail, bg: 'bg-pink-50', iconBg: 'bg-pink-100', iconColor: 'text-pink-600' },
  { key: 'payments', icon: CreditCard, bg: 'bg-blue-50', iconBg: 'bg-blue-100', iconColor: 'text-[#06539B]' },
  { key: 'suppliers', icon: Users, bg: 'bg-teal-50', iconBg: 'bg-teal-100', iconColor: 'text-teal-600' },
] as const

const STATS = [
  { value: '$8K+', label: 'Avg savings per house', labelEs: 'Ahorro promedio por casa', icon: TrendingUp },
  { value: '2-3h', label: 'Saved per week', labelEs: 'Ahorro por semana', icon: Clock },
  { value: '100%', label: 'Bilingual EN/ES', labelEs: 'Bilingüe EN/ES', icon: Globe },
]

const STEPS = [
  { num: '01', icon: Upload, title: 'Upload your plans', titleEs: 'Sube tus planos', desc: 'PDF or photo — AI extracts every material, quantity, and unit in seconds. No manual takeoffs.', descEs: 'PDF o foto — la IA extrae cada material, cantidad y unidad en segundos. Sin cuantificaciones manuales.' },
  { num: '02', icon: Search, title: 'Compare & contract', titleEs: 'Compara y contrata', desc: 'Send RFQs to suppliers, compare quotes side-by-side, detect scope gaps in bids automatically.', descEs: 'Envía cotizaciones a proveedores, compara lado a lado, detecta diferencias en propuestas automáticamente.' },
  { num: '03', icon: Building2, title: 'Build & track', titleEs: 'Construye y da seguimiento', desc: 'Budget tracking, draw schedules, progress photos, insurance alerts — all in one bilingual app.', descEs: 'Presupuesto, calendario de pagos, fotos de avance, alertas de seguros — todo en una app bilingüe.' },
]

// ── COMPONENT ─────────────────────────────────────

export default function LandingPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const isEs = i18n.language.startsWith('es')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const toggleLang = () => {
    const newLang = isEs ? 'en' : 'es'
    i18n.changeLanguage(newLang)
    localStorage.setItem('bluaiq-lang', newLang)
  }

  // Free-trial / sign-up flow lands the user at their real project
  // list. That is the correct destination for the "Iniciar prueba
  // gratis" / "Start free trial" CTAs and the post-Clerk-signup
  // redirect; do not change it.
  const goToApp = () => navigate('/projects')
  // The Demo CTA opens the preloaded demo project directly so
  // prospects explore a fully-populated project (DEMO_DASHBOARD,
  // DEMO_LIFECYCLE, DEMO_QUOTES, etc.) without having to sign up,
  // create their own project, or wade through the "Mis Proyectos"
  // list first. We route through the canonical demo id from
  // demoData so a future demo-id change only needs to land there.
  // (Auth guarding of the destination still applies: a signed-out
  // visitor will bounce through Clerk first. Making the demo route
  // auth-free is a separate, larger product decision.)
  const goToDemo = () => navigate(`/projects/${DEMO_PROJECT.id}`)

  // Intersection observer for reveal animations
  const observerRef = useRef<IntersectionObserver | null>(null)
  const revealCallback = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible')
      }
    })
  }, [])

  useEffect(() => {
    observerRef.current = new IntersectionObserver(revealCallback, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' })
    document.querySelectorAll('.reveal').forEach(el => observerRef.current?.observe(el))
    return () => observerRef.current?.disconnect()
  }, [revealCallback])

  return (
    <div className="min-h-screen bg-brand-bg text-brand-black font-sans">

      {/* ━━━ STICKY NAV ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <nav className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          {/* Logo */}
          {/* white wordmark over the dark hero; brand blue once the nav turns white on scroll */}
          <img src={scrolled ? '/natami_logo.png' : '/natami_logo_white.png'} alt="NATAMI" className="h-[30px] md:h-[38px] w-auto object-contain" />

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-[13px] font-[700] text-brand-muted hover:text-brand-black transition-colors">
              {t('landing.nav.features')}
            </a>
            <a href="#pricing" className="text-[13px] font-[700] text-brand-muted hover:text-brand-black transition-colors">
              {t('landing.nav.pricing')}
            </a>
            <a href="#how" className="text-[13px] font-[700] text-brand-muted hover:text-brand-black transition-colors">
              {t('landing.nav.howItWorks')}
            </a>
            <a href="#faq" className="text-[13px] font-[700] text-brand-muted hover:text-brand-black transition-colors">
              FAQ
            </a>
            <a href="#contact" className="text-[13px] font-[700] text-brand-muted hover:text-brand-black transition-colors">
              {t('landing.nav.contact')}
            </a>
            <button onClick={toggleLang} className="flex items-center gap-1.5 text-[13px] font-[700] text-brand-muted hover:text-brand-black transition-colors">
              <Globe size={14} /> {t('landing.nav.langToggle')}
            </button>
            {/* Demo CTA — opens the demo project directly. Routes
                through `goToDemo` (not `goToApp`) so a prospect
                lands inside a populated demo dashboard rather than
                the generic "Mis Proyectos" list. Free-trial /
                signup flow stays on the SignupCTA below. */}
            <button
              onClick={goToDemo}
              aria-label={t('landing.nav.demoAriaLabel')}
              title={t('landing.nav.demoAriaLabel')}
              className="text-[13px] font-[800] text-brand-gold border border-brand-gold px-4 py-2 rounded-full hover:bg-brand-gold hover:text-white transition-colors flex items-center gap-1.5"
            >
              <Play size={12} /> {t('landing.nav.demo')}
            </button>
            <SignupCTA
              onSignedInClick={goToApp}
              className="bg-brand-gold text-white text-[13px] font-[800] px-6 py-2.5 rounded-full hover:bg-brand-gold-dark transition-colors shadow-lg shadow-brand-gold/20"
            >
              {t('landing.nav.startTrial')}
            </SignupCTA>
          </div>

          {/* Mobile hamburger */}
          <div className="md:hidden flex items-center gap-3">
            <button onClick={toggleLang} className="text-brand-muted">
              <Globe size={18} />
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-brand-border px-5 py-4 space-y-3 shadow-lg">
            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block text-[15px] font-[700] text-brand-muted py-1">
              {t('landing.nav.features')}
            </a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block text-[15px] font-[700] text-brand-muted py-1">
              {t('landing.nav.pricing')}
            </a>
            <a href="#how" onClick={() => setMobileMenuOpen(false)} className="block text-[15px] font-[700] text-brand-muted py-1">
              {t('landing.nav.howItWorks')}
            </a>
            <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="block text-[15px] font-[700] text-brand-muted py-1">
              FAQ
            </a>
            {/* Mobile Demo entry — parity with the desktop Demo
                button. Routes directly into the demo project. */}
            <button
              type="button"
              onClick={() => { setMobileMenuOpen(false); goToDemo() }}
              aria-label={t('landing.nav.demoAriaLabel')}
              className="w-full text-[14px] font-[800] text-brand-gold border border-brand-gold py-3 rounded-full mt-2 flex items-center justify-center gap-1.5"
            >
              <Play size={14} /> {t('landing.nav.demo')}
            </button>
            <SignupCTA
              onSignedInClick={goToApp}
              onBeforeOpen={() => setMobileMenuOpen(false)}
              className="w-full bg-brand-gold text-white text-[14px] font-[800] py-3.5 rounded-full mt-2"
            >
              {t('landing.nav.startTrial')}
            </SignupCTA>
          </div>
        )}
      </nav>

      {/* ============= HERO V2 ============= */}
      <section className="relative min-h-screen overflow-hidden flex flex-col"
        style={{
          background: 'linear-gradient(135deg, #050315 0%, #060270 45%, #14457E 100%)',
          color: '#FFFFFF'
        }}
      >
        {/* Atmospheric radial overlays */}
        <div className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, rgba(6, 83, 155, 0.4) 0%, transparent 60%), radial-gradient(circle at 80% 70%, rgba(245, 166, 35, 0.08) 0%, transparent 50%)'
          }}
        />
        {/* Architectural grid */}
        <div className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
            maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)'
          }}
        />

        {/* Hero content */}
        <div className="relative z-10 flex-1 flex flex-col justify-center max-w-[1280px] mx-auto w-full px-6 md:px-12 lg:px-16 pt-32 md:pt-40 pb-20">
          <div className="flex items-center gap-2.5 mb-6 md:mb-8">
            <span className="w-6 h-px bg-[#5BA8E8]"></span>
            <span className="font-[800] text-[11px] uppercase tracking-[2.5px] text-[#5BA8E8]" style={{ fontFamily: 'Archivo, sans-serif' }}>
              {t('landing.hero.tagline')}
            </span>
          </div>

          <h1 className="font-[900] leading-[0.95] tracking-[-2px] mb-8 md:mb-10 text-white"
            style={{
              fontFamily: 'Archivo, sans-serif',
              fontSize: 'clamp(44px, 9vw, 108px)'
            }}
          >
            <span className="block">{t('landing.hero.titleLine1')}</span>
            <span className="block text-[#5BA8E8]">{t('landing.hero.titleLine2')}</span>
          </h1>

          <p className="max-w-[620px] mb-10 md:mb-13 text-white/80 leading-[1.6]"
            style={{ fontSize: 'clamp(15px, 1.8vw, 19px)' }}
          >
            <Trans
              i18nKey="landing.hero.subtitle"
              components={{ strong: <strong className="text-white font-semibold" /> }}
            />
          </p>

          {/* PR-NEW: hero CTAs now expose both entry paths side-by-side.
              The free-trial / "Create my project" path (SignupCTA →
              goToApp → /projects) is the real-project workflow. The
              Demo path (goToDemo → /projects/${DEMO_PROJECT.id}) is the
              read-only exploration. The descriptive caption below each
              clarifies the intent in plain bilingual copy. The "Ver
              cómo funciona" anchor stays as a scroll affordance below. */}
          <div className="flex flex-col md:flex-row md:flex-wrap gap-y-5 md:gap-y-6 md:gap-x-8 items-start">
            <div className="flex flex-col items-start gap-2">
              <SignupCTA
                onSignedInClick={goToApp}
                className="inline-flex items-stretch font-[800] text-[12px] uppercase tracking-[1px] p-0 bg-transparent border-0 cursor-pointer"
              >
                <span className="bg-white text-[#050315] px-6 py-4 rounded-l-md" style={{ fontFamily: 'Archivo, sans-serif' }}>
                  {t('landing.hero.ctaPrimary')}
                </span>
                <span className="bg-[#F5A623] text-[#050315] px-3.5 py-4 rounded-r-md flex items-center" style={{ fontFamily: 'Archivo, sans-serif' }}>→</span>
              </SignupCTA>
              <p className="text-[12px] leading-[1.5] text-white/70 max-w-[280px]" data-testid="landing-hero-real-path-desc">
                {t('landing.hero.ctaPrimaryDesc')}
              </p>
            </div>
            <div className="flex flex-col items-start gap-2">
              <button
                type="button"
                onClick={goToDemo}
                aria-label={t('landing.nav.demoAriaLabel')}
                title={t('landing.nav.demoAriaLabel')}
                data-testid="landing-hero-demo-cta"
                className="inline-flex items-stretch font-[800] text-[12px] uppercase tracking-[1px] p-0 bg-transparent border-0 cursor-pointer"
                style={{ fontFamily: 'Archivo, sans-serif' }}
              >
                <span className="bg-transparent text-white border border-white/30 border-r-0 px-6 py-4 rounded-l-md flex items-center gap-2">
                  <Play size={12} /> {t('landing.hero.ctaDemo')}
                </span>
                <span className="bg-white/10 text-white border border-white/30 px-3.5 py-4 rounded-r-md flex items-center backdrop-blur">▸</span>
              </button>
              <p className="text-[12px] leading-[1.5] text-white/70 max-w-[280px]" data-testid="landing-hero-demo-path-desc">
                {t('landing.hero.ctaDemoDesc')}
              </p>
            </div>
            <a href="#how" className="inline-flex items-stretch no-underline font-[800] text-[12px] uppercase tracking-[1px] self-start" style={{ fontFamily: 'Archivo, sans-serif' }}>
              <span className="bg-transparent text-white/80 border border-white/15 border-r-0 px-5 py-3.5 rounded-l-md text-[11px]">{t('landing.hero.ctaSecondary')}</span>
              <span className="bg-white/5 text-white/80 border border-white/15 px-3 py-3.5 rounded-r-md flex items-center backdrop-blur text-[11px]">▸</span>
            </a>
          </div>
        </div>

        {/* Floating metric badges */}
        <div className="relative z-10 flex flex-wrap gap-3 px-6 md:px-12 lg:px-16 pb-8 md:pb-12 max-w-[1280px] mx-auto w-full">
          <div className="flex items-center gap-2.5 bg-white/10 border border-white/15 backdrop-blur px-4 py-3 rounded-full text-[13px] font-medium text-white/95">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F5A623]"></span>
            <span>
              <Trans i18nKey="landing.hero.badge1" components={{ strong: <strong className="text-white font-bold" /> }} />
            </span>
          </div>
          <div className="flex items-center gap-2.5 bg-white/10 border border-white/15 backdrop-blur px-4 py-3 rounded-full text-[13px] font-medium text-white/95">
            <span className="w-1.5 h-1.5 rounded-full bg-[#5BA8E8]"></span>
            <span>
              <Trans i18nKey="landing.hero.badge2" components={{ strong: <strong className="text-white font-bold" /> }} />
            </span>
          </div>
          <div className="flex items-center gap-2.5 bg-white/10 border border-white/15 backdrop-blur px-4 py-3 rounded-full text-[13px] font-medium text-white/95">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400"></span>
            <span>
              <Trans i18nKey="landing.hero.badge3" components={{ strong: <strong className="text-white font-bold" /> }} />
            </span>
          </div>
        </div>

        {/* Trust bar */}
        <div className="relative z-10 max-w-[1280px] mx-auto w-full px-6 md:px-12 lg:px-16 py-6 md:py-8 border-t border-white/10">
          <p className="text-center font-[700] text-[10px] uppercase tracking-[2px] text-white/50" style={{ fontFamily: 'Archivo, sans-serif' }}>
            {t('landing.hero.trustBar')}
          </p>
        </div>
      </section>

      {/* ============= PROBLEM ============= */}
      <section className="bg-[#EEEEF6] text-[#050315] px-6 md:px-12 lg:px-16 py-24 md:py-32 lg:py-40">
        <div className="max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          <div>
            <div className="flex items-center gap-2.5 mb-6">
              <span className="w-6 h-px bg-[#06539B]"></span>
              <span className="font-[800] text-[11px] uppercase tracking-[2.5px] text-[#06539B]" style={{ fontFamily: 'Archivo, sans-serif' }}>
                {t('landing.problem.label')}
              </span>
            </div>
            <h2 className="font-[900] leading-[0.98] tracking-[-1.5px] text-[#050315]"
              style={{
                fontFamily: 'Archivo, sans-serif',
                fontSize: 'clamp(36px, 6vw, 72px)'
              }}
            >
              <span className="block">{t('landing.problem.titleLine1')}</span>
              <span className="block">{t('landing.problem.titleLine2')}</span>
              <span className="block text-[#06539B]">{t('landing.problem.titleLine3')}</span>
            </h2>
          </div>
          <div className="max-w-[560px] text-[#2A2840] leading-[1.7]"
            style={{ fontSize: 'clamp(16px, 1.6vw, 19px)' }}
          >
            <p className="mb-6">{t('landing.problem.p1')}</p>
            <p className="mb-6">{t('landing.problem.p2')}</p>
            <p className="mb-6">{t('landing.problem.p3')}</p>
            <p className="pt-3 mt-8 border-t border-[#050315]/10 font-bold text-[#050315]">{t('landing.problem.p4')}</p>
          </div>
        </div>
      </section>

      {/* ============= HOW IT WORKS V2 ============= */}
      <section className="bg-white text-[#050315] px-6 md:px-12 lg:px-16 py-24 md:py-32 lg:py-40 border-t border-[#050315]/5">
        <div className="max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-24">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2.5 mb-6">
              <span className="w-6 h-px bg-[#06539B]"></span>
              <span className="font-[800] text-[11px] uppercase tracking-[2.5px] text-[#06539B]" style={{ fontFamily: 'Archivo, sans-serif' }}>
                {t('landing.howItWorks.label')}
              </span>
            </div>
            <h2 className="font-[900] leading-[1] tracking-[-1.5px]"
              style={{
                fontFamily: 'Archivo, sans-serif',
                fontSize: 'clamp(34px, 5.5vw, 68px)'
              }}
            >
              <span className="block text-[#050315]">{t('landing.howItWorks.titleLine1')}</span>
              <span className="block text-[#050315]/30">{t('landing.howItWorks.titleLine2')}</span>
              <span className="block text-[#06539B]">{t('landing.howItWorks.titleLine3')}</span>
            </h2>
          </div>

          <div className="lg:col-span-7 flex flex-col">
            {([1, 2, 3, 4] as const).map((n, i, arr) => (
              <div
                key={n}
                className={`grid grid-cols-[56px_1fr] gap-5 py-8 border-t border-[#050315]/10 ${i === arr.length - 1 ? 'border-b' : ''}`}
              >
                <div className="font-[800] text-[13px] text-[#06539B] tracking-wider pt-1" style={{ fontFamily: 'Archivo, sans-serif' }}>
                  {`0${n}`}
                </div>
                <div>
                  <h3 className="font-[800] mb-2 text-[#050315] tracking-tight"
                    style={{
                      fontFamily: 'Archivo, sans-serif',
                      fontSize: 'clamp(20px, 2.4vw, 26px)'
                    }}
                  >
                    {t(`landing.howItWorks.role${n}.title`)}
                  </h3>
                  <p className="text-[15px] leading-[1.65] text-[#4A4860] max-w-[560px]">
                    {t(`landing.howItWorks.role${n}.desc`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============= BUILT FOR THE REAL WORLD ============= */}
      <section className="bg-[#EEEEF6] text-[#050315] px-6 md:px-12 lg:px-16 py-24 md:py-32 lg:py-40">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-24 mb-16 lg:mb-20">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-2.5 mb-6">
                <span className="w-6 h-px bg-[#06539B]"></span>
                <span className="font-[800] text-[11px] uppercase tracking-[2.5px] text-[#06539B]" style={{ fontFamily: 'Archivo, sans-serif' }}>
                  {t('landing.useCases.label')}
                </span>
              </div>
              <h2 className="font-[900] leading-[1] tracking-[-1.5px]"
                style={{
                  fontFamily: 'Archivo, sans-serif',
                  fontSize: 'clamp(34px, 5.5vw, 68px)'
                }}
              >
                <span className="block text-[#050315]">{t('landing.useCases.titleLine1')}</span>
                <span className="block text-[#06539B]">{t('landing.useCases.titleLine2')}</span>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:self-end">
              <p className="max-w-[480px] text-[#4A4860] leading-[1.65]"
                style={{ fontSize: 'clamp(15px, 1.5vw, 17px)' }}
              >
                {t('landing.useCases.subtitle')}
              </p>
            </div>
          </div>

          {/*
           * Photo grid is data-driven so the `isEs ? 'es' : 'en'` suffix and
           * `/images/landing-v2/` base path live in exactly one place. Captions
           * are already i18n-backed via `landing.useCases.sceneN` (FIX 5D chunk 5).
           * Real filenames on disk: real-0N-<scene>-{es,en}.png (verified FIX 7).
           */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {[
              { id: '01', scene: 'troca',        captionKey: 'landing.useCases.scene1' },
              { id: '02', scene: 'aprobando',    captionKey: 'landing.useCases.scene2' },
              { id: '03', scene: 'casa',         captionKey: 'landing.useCases.scene3' },
              { id: '04', scene: 'coordinando',  captionKey: 'landing.useCases.scene4' },
            ].map((p) => {
              const src = `/images/landing-v2/real-${p.id}-${p.scene}-${isEs ? 'es' : 'en'}.png`
              const caption = t(p.captionKey)
              return (
                <div key={p.id} className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-[#1A1830] group">
                  <img
                    src={src}
                    alt={caption}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[600ms] group-hover:scale-[1.04]"
                  />
                  <div className="absolute inset-0 pointer-events-none"
                    style={{ background: 'linear-gradient(180deg, transparent 40%, rgba(5, 3, 21, 0.85) 100%)' }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-7 md:p-8 z-[2]">
                    <span className="block font-[800] text-[11px] uppercase tracking-[1.5px] text-white/65 mb-2" style={{ fontFamily: 'Archivo, sans-serif' }}>
                      {p.id}
                    </span>
                    <p className="font-[800] text-white leading-[1.15] tracking-tight"
                      style={{
                        fontFamily: 'Archivo, sans-serif',
                        fontSize: 'clamp(20px, 2.2vw, 26px)'
                      }}
                    >
                      {caption}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ━━━ SOCIAL PROOF STRIP ━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="py-6 px-5 border-y border-brand-border bg-white">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-3">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-12">
            <span className="text-[11px] font-[700] text-brand-muted uppercase tracking-[1px]">
              {t('landing.mission.builtFor')}
            </span>
            {['Frisco', 'McKinney', 'Allen', 'Plano', 'Celina', 'Prosper'].map(city => (
              <span key={city} className="text-[13px] font-[800] text-brand-black/40">{city}</span>
            ))}
          </div>
          <p className="text-[11px] font-[600] text-brand-gold">
            {t('landing.mission.comingSoon')}
          </p>
        </div>
      </section>

      {/* ━━━ FEATURES GRID ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section id="features" className="py-24 px-5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-[36px] md:text-[52px] font-[900] tracking-[-2px] leading-[1.05]">
              {t('landing.features.title')}
            </h2>
            <p className="text-[15px] md:text-[17px] font-[500] text-brand-muted mt-4 max-w-xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-grid">
            {FEATURES.slice(0, 8).map((f, i) => (
              <div key={f.key}
                className="reveal group bg-white rounded-2xl p-6 border border-brand-border hover:border-transparent hover:shadow-xl hover:shadow-black/5 transition-all duration-500 cursor-default hover-lift"
                style={{ transitionDelay: `${i * 50}ms` }}>
                <div className={`w-[48px] h-[48px] ${f.iconBg} flex items-center justify-center mb-4 transition-transform duration-300 animate-morph animate-ping-on-reveal animate-ripple`}>
                  <f.icon size={22} className={`${f.iconColor} animate-icon-float animate-glow animate-magnetic animate-jelly`} style={{ animationDelay: `${i * 0.5}s` }} />
                </div>
                <h3 className="text-[15px] font-[800] text-brand-black transition-colors">
                  {t(`landing.features.items.${f.key}.title`)}
                </h3>
                <p className="text-[12px] font-[500] text-brand-muted mt-1.5 leading-[1.5]">
                  {t(`landing.features.items.${f.key}.desc`)}
                </p>
              </div>
            ))}
          </div>

          {/* Bottom row — 2 wide feature cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            {FEATURES.slice(8).map((f, i) => (
              <div key={f.key}
                className="reveal group bg-white rounded-2xl p-6 border border-brand-border hover:border-transparent hover:shadow-xl hover:shadow-black/5 transition-all duration-500 cursor-default hover-lift flex items-start gap-4">
                <div className={`w-[48px] h-[48px] ${f.iconBg} flex items-center justify-center flex-shrink-0 transition-transform duration-300 animate-morph animate-ripple`}>
                  <f.icon size={22} className={`${f.iconColor} animate-icon-float animate-glow animate-magnetic animate-jelly`} style={{ animationDelay: `${(i + 8) * 0.5}s` }} />
                </div>
                <div>
                  <h3 className="text-[15px] font-[800] text-brand-black transition-colors">
                    {t(`landing.features.items.${f.key}.title`)}
                  </h3>
                  <p className="text-[12px] font-[500] text-brand-muted mt-1.5 leading-[1.5]">
                    {t(`landing.features.items.${f.key}.desc`)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Bilingual + PWA callout */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="reveal group flex items-center gap-5 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-100 rounded-2xl p-6 hover:shadow-lg hover:shadow-blue-100/50 transition-all duration-500 hover:-translate-y-1">
              <div className="w-[52px] h-[52px] rounded-xl bg-gradient-to-br from-[#3B82F6] to-[#00BCD4] flex items-center justify-center flex-shrink-0 animate-float-diagonal group-hover:scale-110 transition-transform duration-300">
                <Globe size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-[15px] font-[800]">
                  {t('landing.features.calloutBilingual.title')}
                </h3>
                <p className="text-[14px] font-[600] text-brand-muted mt-1">
                  {t('landing.features.calloutBilingual.desc')}
                </p>
              </div>
            </div>
            <div className="reveal group flex items-center gap-5 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100 rounded-2xl p-6 hover:shadow-lg hover:shadow-green-100/50 transition-all duration-500 hover:-translate-y-1">
              <div className="w-[52px] h-[52px] rounded-xl bg-gradient-to-br from-[#22C55E] to-[#16A34A] flex items-center justify-center flex-shrink-0 animate-float-diagonal group-hover:scale-110 transition-transform duration-300" style={{ animationDelay: '2s' }}>
                <Smartphone size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-[15px] font-[800]">
                  {t('landing.features.calloutOffline.title')}
                </h3>
                <p className="text-[14px] font-[600] text-brand-muted mt-1">
                  {t('landing.features.calloutOffline.desc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ PRICING ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section id="pricing" className="py-24 px-5">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-[36px] md:text-[52px] font-[900] tracking-[-2px] leading-[1.05]">
              {t('landing.pricing.title')}
            </h2>
            <p className="text-[15px] font-[500] text-brand-muted mt-4 max-w-xl mx-auto">
              {t('landing.pricing.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PLANS.map((p) => (
              <div key={p.key}
                className={`reveal rounded-2xl p-7 relative transition-all duration-500 hover:-translate-y-2 ${
                  p.best
                    ? 'bg-brand-black text-white ring-2 ring-brand-gold scale-[1.03] shadow-2xl shadow-brand-gold/10'
                    : 'bg-white border-2 border-brand-border hover:border-brand-gold/30'
                }`}
              >
                {p.best && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-brand-gold text-white text-[10px] font-[800] uppercase tracking-[1px] px-4 py-1.5 rounded-full">
                    {t('landing.pricing.mostPopular')}
                  </div>
                )}

                <div className="text-[14px] font-[900] uppercase tracking-[1px]">{p.label}</div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-[48px] font-[900] tracking-[-3px]">${p.price}</span>
                  <span className={`text-[14px] font-[600] ${p.best ? 'text-white/50' : 'text-brand-muted'}`}>/mo</span>
                </div>

                <div className="mt-6 space-y-3.5">
                  <PricingFeature label={t('landing.pricing.feature.users', { count: p.users })} best={p.best} />
                  <PricingFeature label={t('landing.pricing.feature.projects', { count: p.projects })} best={p.best} />
                  <PricingFeature label={t(p.ai)} best={p.best} />
                  <PricingFeature label={t('landing.pricing.feature.storage', { value: p.storage })} best={p.best} />
                  {p.key !== 'STARTER' && (
                    <PricingFeature label={t('landing.pricing.feature.expenseSheet')} best={p.best} highlight />
                  )}
                  {p.key === 'BUILDER' && (
                    <PricingFeature label={t('landing.pricing.feature.whatsapp')} best={p.best} highlight />
                  )}
                  <PricingFeature label={t('landing.pricing.feature.pause')} best={p.best} />
                  <PricingFeature label={t('landing.pricing.feature.trial')} best={p.best} />
                </div>

                <SignupCTA
                  onSignedInClick={goToApp}
                  className={`w-full mt-7 text-[14px] font-[800] py-3.5 rounded-full transition-all duration-300 ${
                    p.best
                      ? 'bg-brand-gold text-white hover:bg-brand-gold-dark shadow-lg shadow-brand-gold/25'
                      : 'bg-[#06539B] text-white hover:bg-[#14457E]'
                  }`}
                >
                  {t('landing.pricing.ctaStartFree')}
                </SignupCTA>
              </div>
            ))}
          </div>

          {/* Pause callout — explains the $5 covers data storage costs */}
          <div className="reveal mt-10 bg-gradient-to-r from-brand-gold/5 via-amber-50 to-orange-50 border border-brand-gold/15 rounded-2xl p-7 hover:shadow-lg hover:shadow-amber-100/50 transition-all duration-500">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
              <div className="w-[52px] h-[52px] rounded-xl bg-brand-gold/15 flex items-center justify-center flex-shrink-0 animate-float-diagonal">
                <Pause size={22} className="text-brand-gold" />
              </div>
              <div className="flex-1">
                <h3 className="text-[16px] font-[900] tracking-[-0.3px]">
                  {t('landing.pricing.pause.title')}
                </h3>
                <p className="text-[13px] font-[500] text-brand-muted mt-2 leading-[1.6] max-w-2xl">
                  {t('landing.pricing.pause.desc')}
                </p>
              </div>
              <button onClick={goToApp} className="flex items-center gap-2 text-[13px] font-[800] text-brand-gold hover:text-brand-gold-dark transition-colors whitespace-nowrap">
                {t('landing.pricing.pause.learnMore')} <ArrowRight size={14} />
              </button>
            </div>
          </div>

          {/* Enterprise callout */}
          <div className="reveal mt-8 bg-gradient-to-r from-brand-black to-[#2a2724] rounded-2xl p-7 text-white">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
              <div className="w-[52px] h-[52px] rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                <Building2 size={24} className="text-brand-gold" />
              </div>
              <div className="flex-1">
                <h3 className="text-[16px] font-[900] tracking-[-0.3px]">
                  {t('landing.pricing.enterprise.title')}
                </h3>
                <p className="text-[12px] font-[500] text-white/50 mt-1.5 leading-[1.6] max-w-xl">
                  {t('landing.pricing.enterprise.desc')}
                </p>
              </div>
              <a href="mailto:enterprise@constructai.app"
                className="flex items-center gap-2 bg-brand-gold text-white text-[13px] font-[800] px-6 py-3 rounded-full hover:bg-brand-gold-dark transition-colors whitespace-nowrap shadow-lg shadow-brand-gold/20">
                <MessageSquare size={14} />
                {t('landing.pricing.enterprise.contact')}
              </a>
            </div>
          </div>

          {/* Comparison vs competitors */}
          <div className="mt-8 text-center">
            <p className="text-[12px] font-[700] text-brand-muted">
              {t('landing.pricing.comparison')}
            </p>
          </div>
        </div>
      </section>

      {/* ━━━ FAQ ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <FAQSection />

      {/* ━━━ FINAL CTA ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="py-24 px-5">
        <div className="reveal max-w-3xl mx-auto bg-brand-black rounded-3xl p-10 md:p-16 text-center text-white relative overflow-hidden">
          {/* Decorative gradient circles */}
          <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-brand-gold/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 animate-float-diagonal" />
          <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-brand-gold/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 animate-float" />

          <div className="relative">
            <h2 className="text-[32px] md:text-[48px] font-[900] tracking-[-2px] leading-[1.05]">
              <Trans
                i18nKey="landing.finalCta.title"
                components={{ gold: <span className="text-brand-gold" /> }}
              />
            </h2>
            <p className="text-[15px] font-[500] text-white/50 mt-5 max-w-lg mx-auto leading-[1.6]">
              {t('landing.finalCta.subtitle')}
            </p>
            <SignupCTA
              onSignedInClick={goToApp}
              className="mt-10 bg-brand-gold text-white text-[16px] font-[800] px-12 py-4.5 rounded-full hover:bg-brand-gold-dark transition-all duration-300 shadow-xl shadow-brand-gold/30 hover:shadow-2xl hover:shadow-brand-gold/40 hover:scale-[1.02] flex items-center gap-2.5 mx-auto"
            >
              {t('landing.finalCta.cta')}
              <ArrowRight size={18} />
            </SignupCTA>
            <p className="text-[11px] font-[600] text-white/30 mt-4">
              {t('landing.finalCta.disclaimer')}
            </p>
          </div>
        </div>
      </section>

      {/* ━━━ CONTACT FORM ━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section id="contact" className="py-24 px-5 bg-[#FAFAF8]">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 bg-brand-gold/10 text-brand-gold text-[13px] font-[800] px-4 py-1.5 rounded-full mb-4 uppercase tracking-[0.5px]">
              <MessageSquare size={12} /> {t('landing.contact.tag')}
            </span>
            <h2 className="text-[32px] font-[900] tracking-[-1px] text-brand-black leading-[1.1]">
              {t('landing.contact.title')}
            </h2>
            <p className="text-[14px] font-[600] text-brand-muted mt-3">
              {t('landing.contact.subtitle')}
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* ━━━ FOOTER ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <footer className="border-t border-brand-border py-10 px-5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="flex flex-col items-center md:items-start gap-1">
            <img src="/natami_logo.png" alt="NATAMI" className="h-[22px] w-auto object-contain" />
          </div>
          <div className="flex items-center gap-6 text-[11px] font-[600] text-brand-muted">
            <span>&copy; 2026 NATAMI</span>
            <span>Dallas-Fort Worth, TX</span>
            <button onClick={toggleLang} className="flex items-center gap-1.5 hover:text-brand-black transition-colors">
              <Globe size={12} /> {t('landing.nav.langToggle')}
            </button>
          </div>
        </div>
      </footer>
    </div>
  )
}

// ── Sub-components ────────────────────────────────

function DashCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent: string }) {
  return (
    <div className="bg-[#FAFAF8] rounded-xl p-4">
      <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-brand-muted mb-1.5">{label}</div>
      <div className="text-[20px] md:text-[24px] font-[900] tracking-[-1px]" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[10px] font-[700] text-brand-muted">{sub}</div>}
    </div>
  )
}

function PricingFeature({ label, best, highlight }: { label: string; best: boolean; highlight?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <Check size={14} className={highlight ? 'text-brand-gold flex-shrink-0' : 'text-[#22C55E] flex-shrink-0'} />
      <span className={`text-[14px] font-[600] ${best ? 'text-white/80' : 'text-brand-muted'} ${highlight ? 'font-[700]' : ''}`}>{label}</span>
    </div>
  )
}

function TestimonialCard({ quote, name, role }: { quote: string; name: string; role: string }) {
  return (
    <div className="reveal group bg-brand-bg border border-brand-border rounded-2xl p-6 text-left hover:shadow-lg hover:shadow-black/5 hover:-translate-y-1 transition-all duration-500">
      <div className="flex gap-0.5 mb-4">
        {[1,2,3,4,5].map(i => <Star key={i} size={14} className="text-brand-gold fill-brand-gold group-hover:scale-110 transition-transform duration-300" style={{ transitionDelay: `${i * 50}ms` }} />)}
      </div>
      <p className="text-[13px] font-[600] text-brand-black leading-[1.7]">"{quote}"</p>
      <div className="mt-4 pt-4 border-t border-brand-border flex items-center gap-3">
        <div className="w-[36px] h-[36px] rounded-full bg-brand-gold/10 flex items-center justify-center">
          <Users size={16} className="text-brand-gold" />
        </div>
        <div>
          <div className="text-[12px] font-[800]">{name}</div>
          <div className="text-[10px] font-[600] text-brand-muted">{role}</div>
        </div>
      </div>
    </div>
  )
}

// ── FAQ ──────────────────────────────────────────

// FAQ content lives entirely in locales under landing.faq.items.q1..q10.
// Any reordering or new questions should be made in both src/locales/*.json.
const FAQ_KEYS = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8', 'q9', 'q10'] as const

function FAQSection() {
  const { t } = useTranslation()
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section id="faq" className="py-24 px-5 bg-white">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-[36px] md:text-[52px] font-[900] tracking-[-2px] leading-[1.05]">
            {t('landing.faq.title')}
          </h2>
          <p className="text-[15px] font-[500] text-brand-muted mt-4">
            {t('landing.faq.subtitle')}
          </p>
        </div>

        <div className="space-y-2">
          {FAQ_KEYS.map((key, i) => {
            const isOpen = openIndex === i
            return (
              <div key={key} className="reveal border border-brand-border rounded-xl overflow-hidden bg-brand-bg hover:border-brand-black/20 transition-colors">
                <button
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left"
                >
                  <HelpCircle size={16} className="text-brand-gold flex-shrink-0" />
                  <span className="text-[14px] font-[800] text-brand-black flex-1">
                    {t(`landing.faq.items.${key}.q`)}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`text-brand-muted transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {isOpen && (
                  <div className="px-5 pb-4 pl-12">
                    <p className="text-[13px] font-[500] text-brand-muted leading-[1.7]">
                      {t(`landing.faq.items.${key}.a`)}
                    </p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

// ── CONTACT FORM ──────────────────────────────────

function ContactForm() {
  const { t } = useTranslation()
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', message: '' })
  const [submitted, setSubmitted] = useState(false)
  const [sending, setSending] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSending(true)
    setTimeout(() => {
      setSending(false)
      setSubmitted(true)
    }, 1500)
  }

  if (submitted) {
    return (
      <div className="bg-white border border-[#ECEAE4] rounded-2xl p-10 text-center">
        <div className="w-[64px] h-[64px] rounded-full bg-[#22C55E]/10 flex items-center justify-center mx-auto mb-5">
          <Check size={28} className="text-[#22C55E]" />
        </div>
        <h3 className="text-[20px] font-[900] text-brand-black mb-2">
          {t('landing.contact.success.title')}
        </h3>
        <p className="text-[13px] font-[600] text-brand-muted leading-[1.6]">
          {t('landing.contact.success.desc')}
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-[#ECEAE4] rounded-2xl p-8 space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-[11px] font-[800] text-brand-muted uppercase tracking-[0.5px] mb-1.5 block">
            {t('landing.contact.form.name')}
          </label>
          <input required type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder={t('landing.contact.form.namePlaceholder')}
            className="w-full px-4 py-3 border border-[#ECEAE4] rounded-xl text-[13px] font-[600] focus:outline-none focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold transition-all" />
        </div>
        <div>
          <label className="text-[11px] font-[800] text-brand-muted uppercase tracking-[0.5px] mb-1.5 block">Email *</label>
          <input required type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            placeholder="email@example.com"
            className="w-full px-4 py-3 border border-[#ECEAE4] rounded-xl text-[13px] font-[600] focus:outline-none focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold transition-all" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-[11px] font-[800] text-brand-muted uppercase tracking-[0.5px] mb-1.5 block">
            {t('landing.contact.form.phone')}
          </label>
          <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
            placeholder="(214) 555-0123"
            className="w-full px-4 py-3 border border-[#ECEAE4] rounded-xl text-[13px] font-[600] focus:outline-none focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold transition-all" />
        </div>
        <div>
          <label className="text-[11px] font-[800] text-brand-muted uppercase tracking-[0.5px] mb-1.5 block">
            {t('landing.contact.form.company')}
          </label>
          <input type="text" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
            placeholder={t('landing.contact.form.companyPlaceholder')}
            className="w-full px-4 py-3 border border-[#ECEAE4] rounded-xl text-[13px] font-[600] focus:outline-none focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold transition-all" />
        </div>
      </div>
      <div>
        <label className="text-[11px] font-[800] text-brand-muted uppercase tracking-[0.5px] mb-1.5 block">
          {t('landing.contact.form.message')}
        </label>
        <textarea required rows={4} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
          placeholder={t('landing.contact.form.messagePlaceholder')}
          className="w-full px-4 py-3 border border-[#ECEAE4] rounded-xl text-[13px] font-[600] focus:outline-none focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold transition-all resize-none" />
      </div>
      <button type="submit" disabled={sending}
        className="w-full bg-brand-gold text-white text-[14px] font-[800] py-4 rounded-full hover:bg-brand-gold-dark transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2">
        {sending ? t('landing.contact.form.sending') : t('landing.contact.form.submit')}
        {!sending && <ArrowRight size={16} />}
      </button>
      <p className="text-[10px] font-[600] text-brand-muted text-center">
        {t('landing.contact.form.privacy')}
      </p>
    </form>
  )
}
